/**
 * <hy-product-form>: AJAX-Add-to-Cart für snippets/hy-product-form.liquid.
 * Reine Erweiterung — ohne dieses Modul (oder bei jedem Fehler:
 * Netzwerk, eine nicht lesbare Antwort) sendet das Formular klassisch ab
 * und leitet zur Warenkorbseite weiter, das Standardverhalten von
 * Shopifys eigenem {% form 'product' %}. Mit diesem Modul: POST an
 * /cart/add.js, die Warenkorb-Zahl im Header wird über die Section
 * Rendering API mitaktualisiert (inklusive ihres aria-label, das die Zahl
 * trägt — der ganze Link-Knoten wird ersetzt, kein Textknoten-Patch), eine
 * sichtbare Statuszeile meldet Erfolg/Fehler, ein hy:cart:updated-Event
 * informiert andere Komponenten. Seit M4 AP-64: öffnet sich der Cart Drawer
 * als Reaktion darauf, kündigt dessen Dialog den Erfolg selbst an — die
 * Statuszeile hier bleibt dann stumm, um keine zweite, zeitgleiche Ansage
 * zu erzeugen (siehe #drawerWillOpen).
 *
 * Keine Preisberechnung im Client (CLAUDE.md Regel 8): der Erfolgstext
 * nennt nur den Produkttitel, keinen Preis; die 422-Fehlermeldung ist
 * Shopifys eigene, bereits lokalisierte description — nie ein selbst
 * gebauter Text mit eigener Mengen-/Preislogik.
 */
/**
 * One /cart.js request shared by every limited form (AP-295): all of them
 * react to the same hy:cart:updated event synchronously, so a collection
 * page with many quick-add cards asks the server once, not once per card.
 */
let sharedCartRequest = null;
function fetchCartOnce() {
  if (!sharedCartRequest) {
    sharedCartRequest = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .finally(() => {
        sharedCartRequest = null;
      });
  }
  return sharedCartRequest;
}

class HyProductForm extends HTMLElement {
  #controller = null;
  // Maximum quantity per order (AP-295): data-quantity-limit / data-in-cart /
  // data-product-id from snippets/hy-product-form.liquid and the quick add in
  // snippets/hy-product-card.liquid. 0 = no limit.
  #limit = 0;
  #inCart = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#limit = Number(this.dataset.quantityLimit) || 0;
    this.#inCart = Number(this.dataset.inCart) || 0;
    this.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
    this.addEventListener('click', this.#handleStepper, { signal: this.#controller.signal });
    this.#armSteppers();

    if (this.#limit) {
      this.addEventListener('change', this.#handleQuantityInput, { signal: this.#controller.signal });
      // Any cart change (this form, another form, the drawer) moves the room
      // left under the limit.
      document.addEventListener('hy:cart:updated', this.#refreshInCart, {
        signal: this.#controller.signal,
      });
    }

    // Nach einem Variantenwechsel ersetzt assets/hy-variant-picker.js das
    // Formular komplett — die frisch eingesetzten Schaltflächen tragen das
    // Freigabe-Attribut dann noch nicht und blieben unsichtbar.
    document.addEventListener(
      'hy:variant:changed',
      () => {
        this.#armSteppers();
        this.#detachCartAction();
      },
      { signal: this.#controller.signal },
    );
    this.#detachCartAction();
  }

  /**
   * Single add_to_cart in analytics (AP-296). Shopify's analytics counts an
   * add to cart twice when a form with action="/cart/add" is submitted AND
   * the add then goes out as /cart/add.js: once on the submit, once on the
   * request (measured live on Düperthal: two product_added_to_cart, two GA4
   * add_to_cart per click). With this module the add always goes through
   * /cart/add.js, so the action is parked in data-hy-action while the module
   * runs and restored only for the classic fallback (#submitClassic).
   * Without JavaScript the form keeps its action and posts as before.
   * Also stops a blocked click (maximum quantity, AP-295) from counting.
   */
  #detachCartAction() {
    for (const form of this.querySelectorAll('form[action*="/cart/add"]')) {
      form.dataset.hyAction = form.getAttribute('action');
      form.removeAttribute('action');
    }
  }

  #submitClassic(form) {
    if (form.dataset.hyAction) form.setAttribute('action', form.dataset.hyAction);
    form.submit();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Gibt die Stepper-Schaltflächen frei. Sie sind per CSS ausgeblendet, bis
   * dieses Attribut gesetzt ist — ohne JavaScript soll kein Knopf sichtbar
   * sein, der nichts tut. Das Zahlenfeld bleibt in beiden Fällen bedienbar.
   *
   * Wird auch nach einem Variantenwechsel erneut gebraucht: Das Formular wird
   * dabei komplett ersetzt, die neuen Knöpfe tragen das Attribut noch nicht.
   */
  #armSteppers() {
    for (const el of this.querySelectorAll('[data-hy-stepper]')) {
      el.setAttribute('data-hy-stepper-ready', '');
    }
  }

  #handleStepper = (event) => {
    const button = event.target.closest('[data-hy-stepper-step]');
    if (!button || !this.contains(button)) return;

    const input = button.parentElement?.querySelector('input[type="number"]');
    if (!input) return;

    // Maximum quantity per order reached: plus shows the note (AP-295).
    if (Number(button.dataset.hyStepperStep) > 0 && this.#exceedsLimit((Number(input.value) || 0) + (Number(input.step) || 1))) {
      this.#revealLimitNote();
      return;
    }

    // Über stepUp/stepDown statt eigener Rechnung: Der Browser kennt min, max
    // und step aus dem Markup und hält sie von sich aus ein. Genau diese Werte
    // stammen aus variant.quantity_rule plus den Mengen-Metafeldern
    // (snippets/hy-quantity-input.liquid) — eine eigene Rechnung hier müsste
    // dieselbe Logik doppeln und könnte davon abweichen.
    const direction = Number(button.dataset.hyStepperStep);
    try {
      if (direction > 0) input.stepUp();
      else input.stepDown();
    } catch {
      // stepUp/stepDown werfen, wenn der aktuelle Wert nicht zur Schrittfolge
      // passt (etwa nach manueller Eingabe). Dann auf den nächstgültigen Wert
      // zurückfallen, statt die Schaltfläche wirkungslos zu lassen.
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 0;
      const current = Number(input.value) || min;
      input.value = String(Math.max(min, current + direction * step));
    }

    // 'change' statt 'input': Für Formularlogik ist das der Ereignistyp, den
    // eine abgeschlossene Werteänderung auslöst.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    event.preventDefault();

    const submitButton = form.querySelector('button[name="add"]');

    // Maximum quantity per order (AP-295): nothing is added above it.
    const requested = Number(form.querySelector('[name="quantity"]')?.value) || 1;
    if (this.#exceedsLimit(requested)) {
      this.#limitReached(submitButton);
      return;
    }

    const statusEl = this.querySelector('.hy-product-form__status');
    const headerId = document.querySelector('.hy-header')?.id.replace('shopify-section-', '');

    const formData = new FormData(form);
    formData.append('sections', this.#sectionIds().join(','));
    formData.append('sections_url', window.location.pathname + window.location.search);

    submitButton?.setAttribute('aria-busy', 'true');
    if (statusEl) {
      statusEl.textContent = '';
      statusEl.removeAttribute('data-hy-status');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(async (response) => {
        const responseJson = await response.json();

        if (!response.ok) {
          this.#showStatus(statusEl, responseJson.description || responseJson.message, 'error');
          return;
        }

        // Count the added units right away (AP-295): a second click before
        // the /cart.js refresh below must not slip past the limit.
        if (this.#limit) this.#inCart += requested;
        this.#updateHeaderCart(responseJson.sections, headerId);
        // Öffnet sich der Cart Drawer (AP-64), kündigt der Dialog selbst
        // den Erfolg an (Titel + aria-describedby) — eine zweite,
        // zeitgleiche Ansage aus dieser Statuszeile würde sich mit dem
        // Fokuswechsel in den Dialog überschneiden und beide Ansagen
        // halb abschneiden. Ohne Drawer (Setting aus, Sektion entfernt,
        // auf /cart) bleibt diese Zeile die einzige Ansage — unverändert.
        if (!this.#drawerWillOpen()) {
          this.#showStatus(statusEl, this.#successMessage(responseJson), 'success');
        }
        document.dispatchEvent(
          new CustomEvent('hy:cart:updated', {
            detail: { cart: responseJson, sections: responseJson.sections, trigger: submitButton },
          }),
        );
        // Eigenes Event für GA4 add_to_cart (M6 AP-95): hy:cart:updated feuert
        // auch bei Mengen-/Entfernen-Mutationen aus dem Drawer, hier braucht es
        // die eindeutige „hinzugefügt"-Semantik. detail.item ist das
        // /cart/add.js-Response-Objekt (nacktes Zeilenobjekt bei legacy id+add).
        document.dispatchEvent(
          new CustomEvent('hy:product:added', { detail: { item: responseJson } }),
        );
      })
      .catch(() => {
        this.#submitClassic(form);
      })
      .finally(() => {
        submitButton?.removeAttribute('aria-busy');
      });
  };

  /** True when adding `quantity` would take the product above its limit. */
  #exceedsLimit(quantity) {
    return this.#limit > 0 && this.#inCart + quantity > this.#limit;
  }

  #revealLimitNote() {
    this.querySelector('[data-hy-quantity-limit-note] > [hidden]')?.removeAttribute('hidden');
  }

  #hideLimitNote() {
    this.querySelector('[data-hy-quantity-limit-note] > p')?.setAttribute('hidden', '');
  }

  /**
   * Product page: the note below the buy row. Quick add on a card: the cart
   * drawer opens and shows it at the product's line
   * (assets/hy-cart-drawer.js, hy:quantity-limit:reached).
   */
  #limitReached(trigger) {
    // Quick add on a card: the cart drawer shows the note at the product's
    // line and cancels the event. Nobody handled it (no drawer, on the cart
    // page, line not found): fall back to the card's own note below.
    if (this.hasAttribute('data-limit-via-drawer')) {
      const handled = !document.dispatchEvent(
        new CustomEvent('hy:quantity-limit:reached', {
          cancelable: true,
          detail: { productId: this.dataset.productId, trigger },
        }),
      );
      if (handled) return;
    }
    const note = this.querySelector('[data-hy-quantity-limit-note]');
    if (note) {
      this.#revealLimitNote();
      // The sticky buy bar (assets/hy-sticky-atc.js) clicks this form's
      // button while the form itself may be scrolled away: bring the note
      // into view, otherwise the click seems to do nothing.
      const rect = note.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        note.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      }
      return;
    }
  }

  /** Typed quantity above the room left: clamp it and show the note. */
  #handleQuantityInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'quantity') return;
    const room = Math.max(this.#limit - this.#inCart, Number(input.min) || 1);
    if (Number(input.value) > room) {
      input.value = String(room);
      this.#revealLimitNote();
    }
  };

  /**
   * Re-reads how many units of the product the cart holds. /cart/change.js
   * answers with the whole cart (drawer, cart page); /cart/add.js only with
   * the added line, then /cart.js is asked. Updates the field's max so the
   * stepper and the note agree with the cart.
   */
  #refreshInCart = async (event) => {
    // Only a whole cart counts: /cart/add.js answers with the added line(s)
    // alone, which would read as "nothing else in the cart".
    const cart = event?.detail?.cart;
    let items = cart && 'item_count' in cart ? cart.items : null;
    if (!Array.isArray(items)) {
      items = (await fetchCartOnce())?.items;
      if (!Array.isArray(items)) return;
    }
    const productId = Number(this.dataset.productId);
    this.#inCart = items
      .filter((item) => item.product_id === productId)
      .reduce((sum, item) => sum + item.quantity, 0);

    const input = this.querySelector('input[name="quantity"]');
    if (input) {
      const room = this.#limit - this.#inCart;
      input.dataset.hyLimitRoom = String(room);
      input.max = String(Math.max(room, Number(input.min) || 1));
      if (Number(input.value) > Number(input.max)) input.value = input.max;
    }
    this.#hideLimitNote();
  };

  #drawerWillOpen() {
    const drawer = document.querySelector('hy-cart-drawer');
    return !!drawer && drawer.dataset.openOnAdd === 'true' && !document.querySelector('.hy-main-cart');
  }

  // DOM-getrieben statt einer festen Liste: liefert automatisch die
  // richtigen Sektions-IDs unabhängig davon, ob ein Cart Drawer überhaupt
  // vorhanden ist (Section Group vom Händler entfernt), und ohne dass
  // dieses Modul den Drawer kennen oder importieren müsste.
  #sectionIds() {
    const ids = [];
    const headerId = document.querySelector('.hy-header')?.id.replace('shopify-section-', '');
    if (headerId) ids.push(headerId);
    const drawerId = document.querySelector('hy-cart-drawer')?.dataset.sectionId;
    if (drawerId) ids.push(drawerId);
    return ids;
  }

  #updateHeaderCart(sections, headerId) {
    if (!sections || !headerId || !sections[headerId]) return;

    const responseDoc = new DOMParser().parseFromString(sections[headerId], 'text/html');
    const newCartLink = responseDoc.querySelector('.hy-header__cart-link');
    const currentCartLink = document.querySelector('.hy-header__cart-link');
    if (newCartLink && currentCartLink) currentCartLink.replaceWith(newCartLink);
  }

  #successMessage(item) {
    const prefix = this.dataset.addedPrefix || '';
    const suffix = this.dataset.addedSuffix || '';
    return `${prefix}${item.title || ''}${suffix}`;
  }

  #showStatus(statusEl, message, kind) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.setAttribute('data-hy-status', kind);
  }
}

customElements.define('hy-product-form', HyProductForm);
